import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { httpsCallable } from "firebase/functions";
import { useEffect, useRef, useState } from "react";
import { functions } from "../../App";

type BackfillResult = {
  classesScanned: number;
  createdPretests: number;
  createdPosttests: number;
  skippedClasses: number;
  classesNeedingUpdates?: number;
  dryRun?: boolean;
};

const BackfillDefaultPrePostAssignments: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [preview, setPreview] = useState<BackfillResult | null>(null);
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewLoading, setPreviewLoading] = useState(true);
  const isMountedRef = useRef(true);

  const callBackfill = async (dryRun: boolean) => {
    const callable = httpsCallable(functions, "adminBackfillDefaultPrePostAssignments");
    const response = await callable({ dryRun });
    return response.data as BackfillResult;
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewMessage("");
    try {
      const callable = httpsCallable(functions, "adminBackfillDefaultPrePostAssignments");
      const response = await callable({ dryRun: true });
      if (!isMountedRef.current) {
        return;
      }
      setPreview(response.data as BackfillResult);
      setPreviewMessage("");
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setPreviewMessage(`Preview failed: ${String(error)}`);
    } finally {
      if (isMountedRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  const runBackfill = async () => {
    if (running) {
      return;
    }
    setRunning(true);
    setResultMessage("");
    setResult(null);
    try {
      const payload = await callBackfill(false);
      setResult(payload);
      setResultMessage("Backfill completed successfully.");
      await loadPreview();
    } catch (error) {
      setResultMessage(`Backfill failed: ${String(error)}`);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    loadPreview().catch(() => undefined);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Pre/Post-test Assignment Backfill</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>
          Creates missing Financial Literacy Pre-test and Post-test assignments for classes created within the last 30 days.
        </p>
        {previewLoading && <p>Loading preview...</p>}
        {preview && (
          <>
            <p><b>Preview classes scanned:</b> {preview.classesScanned}</p>
            <p><b>Preview classes needing updates:</b> {preview.classesNeedingUpdates ?? 0}</p>
            <p><b>Preview pre-tests to create:</b> {preview.createdPretests}</p>
            <p><b>Preview post-tests to create:</b> {preview.createdPosttests}</p>
          </>
        )}
        {previewMessage && <p>{previewMessage}</p>}
        {result && (
          <>
            <p><b>Classes scanned:</b> {result.classesScanned}</p>
            <p><b>Pre-tests created:</b> {result.createdPretests}</p>
            <p><b>Post-tests created:</b> {result.createdPosttests}</p>
            <p><b>Classes skipped:</b> {result.skippedClasses}</p>
          </>
        )}
        <IonButton
          expand="block"
          onClick={runBackfill}
          disabled={running}
          aria-label="Run pre/post-test assignment backfill"
          title="Run pre/post-test assignment backfill"
        >
          {running ? "Running..." : "Run Backfill"}
        </IonButton>
        {resultMessage && <p>{resultMessage}</p>}
      </IonCardContent>
    </IonCard>
  );
};

export default BackfillDefaultPrePostAssignments;
